import {
    Grid,
    IconButton,
    InputBase,
    InputBaseComponentProps,
    Popover,
    SxProps,
    Theme,
    Tooltip,
    Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import WarningIcon from "@mui/icons-material/Warning";
import { Skeleton } from "@mui/material";
import React, { CSSProperties, ReactNode, useImperativeHandle } from "react";
import theme from "Theme/AppTheme";
import hexToRGB from "utils/hexToRGB";
import AcxInputLabel from "./AcxInputLabel";
import { sxClsx } from "utils/styleClsx";
import { ContentCopyRounded } from "@mui/icons-material";
import AcxButton from "components/UI/AcxButton";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import copy from "copy-to-clipboard";

/**
 * Props for the AcxMainTextFieldGrid component.
 *
 * @property {SxProps<any>} [textContainerSx] - Additional styles to apply to the text field container, merged with the base container styles.
 * @property {SxProps<any>} [textContainerSxOverride] - Styles to apply to the text field container, overriding and disabling the base container styles (such as borders).
 */
interface IAcxTextFieldProps {
    labelText?: string | undefined;
    error?: boolean;
    hideErrorIcon?: boolean;
    errorHelper?: string;
    placeholderText?: string;
    id: string;
    value?: string | number;
    helperText?: string | undefined;
    inputProps?: InputBaseComponentProps;
    required?: boolean;
    type?:
        | string
        | (
              | "text"
              | "time"
              | "url"
              | "tel"
              | "number"
              | "email"
              | "date"
              | "password"
          );
    onChange?: (evt: React.ChangeEvent<HTMLInputElement>) => void;
    onKeyDown?: (
        evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    containerStyle?: CSSProperties | undefined;
    containerId?: string;
    focus?: boolean;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
    helpPopperText?: string | ReactNode;
    autofocus?: boolean;
    readonly?: boolean;
    shrink?: boolean;
    infoText?: string;
    multiline?: boolean;
    /**
     * Optional for backwards compatability. Adds left padding when that is not present
     * when using multiline text fields
     */
    multilinePadding?: boolean;
    rows?: number;
    rowsMin?: number;
    rowsMax?: number;
    rightAlignInputText?: boolean;
    centerAlignInputText?: boolean;
    showAllErrors?: boolean;
    skeleton?: boolean;
    onBlur?: (val?: string | number) => void;
    onFocus?: (val?: string | number) => void;
    onClick?: React.MouseEventHandler<HTMLElement>;
    onLeaveRight?: () => void;
    onLeaveLeft?: () => void;
    textareaRootStyles?: CSSProperties;
    fullWidth?: boolean;
    isDisabled?: boolean;
    maxLength?: number;
    /**
     * If set to true, this will prevent a text field of type number from auto-setting itself to 0 if the value is blank.
     */
    insertBlankIfNumber?: boolean;
    disablePadding?: boolean;
    blurOnEnterPress?: boolean;
    autoComplete?: string;
    labelSx?: SxProps<Theme>;
    labelSxImportant?: SxProps<Theme>;
    sx?: SxProps<Theme>;
    labelEndText?: string;
    resizable?: boolean;
    textContainerSx?: CSSProperties;
    textContainerSxOverride?: CSSProperties;
    showCopyButton?: boolean;
    showPasswordToggle?: boolean;
}

export type AcxMainTextFieldRef = HTMLInputElement | HTMLTextAreaElement;

const AcxMainTextField = React.forwardRef<
    AcxMainTextFieldRef,
    IAcxTextFieldProps
>(
    (
        {
            error = false,
            labelText = "",
            placeholderText = "",
            value: propValue = "",
            required = false,
            type: inputType = "text",
            containerStyle = {},
            readonly = false,
            shrink = false,
            multiline = false,
            rowsMin = 2,
            rowsMax = 4,
            ...props
        },
        ref,
    ) => {
    const [focused, setFocused] = React.useState(false);
    const [helpAnchorEl, setHelpAnchorEl] =
        React.useState<HTMLButtonElement | null>(null);
    const [showPassword, setShowPassword] = React.useState(false);

    const [value, setValue] = React.useState<string | number>(
        inputType === "number" && !props.insertBlankIfNumber ? 0 : "",
    );

    const inputRef = React.useRef<AcxMainTextFieldRef>(null);
    useImperativeHandle(ref, () => inputRef.current!, []);

    React.useEffect(() => {
        if (propValue !== value) {
            setValue(
                inputType === "number" && !props.insertBlankIfNumber
                    ? propValue || 0
                    : propValue ?? "",
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propValue]);

    React.useEffect(() => {
        if (props.focus === true && inputRef.current) {
            inputRef.current.focus?.();
            inputRef.current.selectionStart = 0;
        }
    }, [props.focus]);

    const onBlur = () => {
        setFocused(false);
        props.onBlur?.(
            inputType === "number" && !props.insertBlankIfNumber
                ? Number(value || 0)
                : value,
        );
    };
    const onFocus = () => {
        setFocused(true);
        props.onFocus?.(value);
    };
    const onKeyPress = (
        event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        if (event && event.currentTarget.selectionStart) {
            if (event.key === "ArrowRight") {
                if (
                    value?.toString().length -
                        event.currentTarget.selectionStart ===
                    0
                ) {
                    props.onLeaveRight?.();
                }
            }
            if (event.key === "ArrowLeft") {
                if (event.currentTarget.selectionStart === 1) {
                    props.onLeaveLeft?.();
                }
            }
            if (props.blurOnEnterPress && event.key === "Enter") {
                onBlur();
                inputRef.current?.blur();
            }
        }

        if (event) {
            props.onKeyDown?.(event);
        }
    };
    const getHelpPopperContent = () => {
        if (props.helpPopperText) {
            if (React.isValidElement(props.helpPopperText)) {
                return props.helpPopperText;
            } else {
                return <div>{props.helpPopperText}</div>;
            }
        }
        return null;
    };
    const handleHelpClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setHelpAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setHelpAnchorEl(null);
    };
    const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setValue(evt.currentTarget.value);
        props.onChange?.(evt);
    };
    const open = Boolean(helpAnchorEl);

    let endAdornment: ReactNode = undefined;
    let type = inputType;
    if (showPassword && inputType === "password") {
        type = "text";
    } else if (inputType === "password") {
        type = "password";
    }

    if (props.endAdornment) {
        endAdornment = props.endAdornment;
    } else if (props.helpPopperText) {
        endAdornment = (
            <IconButton onClick={handleHelpClick} size="small">
                <HelpOutlineIcon fontSize="small" />
            </IconButton>
        );
    }
    if (props.showPasswordToggle && inputType === "password") {
        endAdornment = (
            <AcxButton
                onClick={() => setShowPassword(!showPassword)}
                color="white"
                sx={(theme) => ({
                    color: theme.palette.gray[400],
                    border: "none",
                })}
            >
                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
            </AcxButton>
        );
    }

    return (
        <div style={containerStyle} id={props.containerId}>
            <div
                style={
                    props.labelEndText || props.showCopyButton
                        ? {
                              display: "flex",
                              justifyContent: "space-between",
                              opacity: 1,
                          }
                        : { opacity: 1 }
                }
            >
                {labelText && (
                    <AcxInputLabel
                        htmlFor={props.id}
                        id={`${props.id}-label`}
                        labelText={labelText}
                        focused={focused}
                        shrink={shrink}
                        infoText={props.infoText}
                        sx={sxClsx(
                            {
                                fontFamily: theme.typography.fontFamily,
                                color: theme.palette.text.primary,
                                fontSize: "12px",
                                lineHeight: "16px",
                            },
                            props.labelSx,
                            focused && {
                                color: theme.palette.primary.main,
                                fontWeight: theme.typography.fontWeightBold,
                            },
                            required &&
                                !inputRef.current?.value && {
                                    "&:after": {
                                        color: theme.palette.red.main,
                                        content: `" *"`,
                                    },
                                },
                            props.labelSxImportant,
                        )}
                    />
                )}
                {props.labelEndText && (
                    <Typography
                        color={theme.palette.gray[400]}
                        fontWeight={400}
                    >
                        {props.labelEndText}
                    </Typography>
                )}
                {props.showCopyButton && (
                    <IconButton
                        size="small"
                        sx={{ fontSize: "16px" }}
                        onClick={() => {
                            if (value) {
                                copy(value.toString());
                            }
                        }}
                        disabled={value === "" || value === undefined}
                    >
                        <ContentCopyRounded fontSize="inherit" />
                    </IconButton>
                )}
            </div>
            {props.skeleton ? (
                <Skeleton
                    variant="rectangular"
                    width={"100%"}
                    height={32}
                    animation="wave"
                    style={{ borderRadius: 4 }}
                />
            ) : (
                <Grid
                    onClick={props.onClick}
                    sx={sxClsx(
                        !props.textContainerSxOverride && {
                            border: "1px solid",
                            backgroundColor: theme.palette.white.main,
                            borderColor: theme.palette.lightgrayBorder.main,
                            borderRadius: `${theme.shape.borderRadius}px`,
                            display: "flex",
                            alignItems: "center",
                            ...(multiline
                                ? {} // Don't set maxHeight for multiline
                                : { maxHeight: "32px" }),
                            "&:Focused": {
                                borderColor: hexToRGB(
                                    theme.palette.primary.main,
                                    0.7,
                                ),
                            },
                        },
                        !props.fullWidth &&
                            (props.textareaRootStyles ?? {
                                marginRight: "0.5rem",
                            }),
                        props.textContainerSx,
                        props.textContainerSxOverride,
                        focused && {
                            borderColor: hexToRGB(
                                theme.palette.primary.main,
                                0.7,
                            ),
                        },
                        error && {
                            borderWidth: "2px",
                            borderColor: theme.palette.error.main,
                        },
                    )}
                >
                    <InputBase
                        disabled={props.isDisabled ?? false}
                        fullWidth={props.fullWidth ?? false}
                        id={props.id}
                        inputProps={{
                            ...{ type },
                            ...props.inputProps,
                            maxLength: props.maxLength,
                            sx: sxClsx(
                                { paddingTop: "7px", paddingBottom: "7px" },
                                props.rightAlignInputText && {
                                    textAlign: "right",
                                },
                                props.centerAlignInputText && {
                                    textAlign: "center",
                                },
                                multiline &&
                                    props.resizable && { resize: "vertical" },
                            ),
                        }}
                        placeholder={placeholderText}
                        startAdornment={props.startAdornment}
                        sx={sxClsx(
                            {
                                border: "none",
                                borderWidth: 0,
                                outlineWidth: 0,
                                backgroundColor: "transparent",
                                flex: "1 1 auto",
                                fontSize: "14px",
                                lineHeight: "20px",
                                fontFamily: theme.typography.fontFamily,
                                paddingLeft: props.disablePadding
                                    ? "0px"
                                    : theme.spacing(1),
                                alignItems: "center",
                            },
                            multiline &&
                                props.multilinePadding && { paddingLeft: 1 },
                            props.sx,
                            focused && {
                                caretColor: theme.palette.primary.main,
                            },
                            props.rightAlignInputText && {
                                paddingLeft: "0px",
                                paddingRight: theme.spacing(1),
                            },
                        )}
                        inputRef={inputRef}
                        type={type}
                        autoFocus={props.autofocus}
                        autoComplete={props.autoComplete}
                        value={value}
                        onFocus={onFocus}
                        onBlur={onBlur}
                        onChange={onChange}
                        onKeyDown={onKeyPress}
                        readOnly={readonly}
                        endAdornment={endAdornment}
                        multiline={multiline}
                        minRows={
                            multiline
                                ? props.rows ?? rowsMin
                                : undefined
                        }
                        maxRows={multiline ? rowsMax : undefined}
                    />

                    {error &&
                        !props.rightAlignInputText &&
                        !props.hideErrorIcon && (
                            <Tooltip title={props.errorHelper || "Error"}>
                                <WarningIcon
                                    sx={{
                                        color: theme.palette.red.main,
                                        marginRight: theme.spacing(1),
                                    }}
                                />
                            </Tooltip>
                        )}
                </Grid>
            )}
            {props.helperText !== undefined && (
                <AcxInputLabel
                    id={"AcxMainText-BottomLabel"}
                    sx={sxClsx(
                        {
                            fontFamily: theme.typography.fontFamily,
                            color: theme.palette.text.primary,
                            fontSize: "12px",
                            lineHeight: "16px",
                        },
                        error && {
                            color: `${theme.palette.error.main} !important`,
                        },
                    )}
                    showAllErrors={!!props.showAllErrors}
                >
                    {props.helperText}
                </AcxInputLabel>
            )}
            <Popover
                id={"help-popover-" + props.id}
                open={open}
                anchorEl={helpAnchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
                {getHelpPopperContent()}
            </Popover>
        </div>
    );
},
);

export default AcxMainTextField;
